import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  type ImportedInventoryItemResult as ImportedInventoryItemResultProto,
  InventoryItem as InventoryItemProto,
  InventoryItemJobProgressionBillingInfo as InventoryItemJobProgressionBillingInfoProto,
  InventoryItemJobProgressionEssential as InventoryItemJobProgressionEssentialProto,
  InventoryItemJobProgressionOutsourcing as InventoryItemJobProgressionOutsourcingProto,
  InventoryItemJobProgressionPreparation as InventoryItemJobProgressionPreparationProto,
  InventoryItemJobProgressionRetail as InventoryItemJobProgressionRetailProto,
  InventoryItemJobProgressionReview as InventoryItemJobProgressionReviewProto,
  InventoryItemSupportingItem as InventoryItemSupportingItemProto,
  InventoryItemTypeSpecification as InventoryItemTypeSpecificationProto,
  ProgressionStepBillingValues as ProgressionStepBillingValuesProto,
} from '@taxfyle/api-internal/internal/inventory_item_pb'
import { Decimal as DecimalProto } from '@taxfyle/api-internal/shared/types/decimal_pb'
import { Id } from '@/infra/id'

/**
 * A service item as an export carries it: every field of the item, so that it
 * can be imported into another workspace.
 */
export type InventoryItemExport = InventoryItemProto.AsObject

/**
 * Which item an imported service item became in the importing workspace.
 */
export interface ImportedInventoryItem {
  previousItemId: string
  newItemId: string
}

/**
 * Exports made with an older `@taxfyle/api-internal` (HQ's) name the refund
 * method specs with an `Info` suffix. They are the same fields (5 and 7).
 */
type InventoryItemSupportingItemSpecExport =
  InventoryItemSupportingItemProto.Spec.AsObject & {
    personalRefundMethodInfo?: object
    businessRefundMethodInfo?: object
  }

/**
 * Maps which item an imported service item became.
 *
 * @param proto
 */
export function mapImportedInventoryItemResultProtoToImportedInventoryItem(
  proto: ImportedInventoryItemResultProto,
): ImportedInventoryItem {
  return {
    previousItemId: Id.assert(proto.getPreviousItemId()),
    newItemId: Id.assert(proto.getNewItemId()),
  }
}

/**
 * Maps a service item to its export.
 *
 * @param proto
 */
export function mapInventoryItemProtoToExport(
  proto: InventoryItemProto,
): InventoryItemExport {
  return proto.toObject()
}

/**
 * Rebuilds the service item message from its export.
 *
 * @param item
 */
export function mapInventoryItemExportToProto(
  item: InventoryItemExport,
): InventoryItemProto {
  const proto = new InventoryItemProto()
    .setId(item.id)
    .setRootId(item.rootId)
    .setArchived(item.archived)
    .setProVisibility(item.proVisibility)
    .setName(item.name)
    .setDescription(item.description)
    .setVersion(item.version)
    .setCourtesyAmount(item.courtesyAmount)
    .setPrice(mapDecimalExportToProto(item.price))
    .setProPrice(mapDecimalExportToProto(item.proPrice))
    .setCreatedBy(item.createdBy)
    .setShortId(item.shortId)
    .setPriceType(item.priceType)
    .setVersionId(item.versionId)
    .setSupportingItemsList(
      (item.supportingItemsList ?? []).flatMap(mapSupportingItemExportToProto),
    )

  if (item.dateCreated) {
    proto.setDateCreated(mapTimestampExportToProto(item.dateCreated))
  }
  if (item.typeSpecification) {
    proto.setTypeSpecification(
      mapTypeSpecificationExportToProto(item.typeSpecification),
    )
  }
  if (item.dateArchived && item.archivedBy) {
    proto
      .setDateArchived(mapTimestampExportToProto(item.dateArchived))
      .setArchivedBy(new StringValue().setValue(item.archivedBy.value))
  }
  if (item.dateUpdated && item.updatedBy) {
    proto
      .setDateUpdated(mapTimestampExportToProto(item.dateUpdated))
      .setUpdatedBy(new StringValue().setValue(item.updatedBy.value))
  }
  if (item.workspaceId) {
    proto.setWorkspaceId(item.workspaceId)
  }
  return proto
}

/**
 * A supporting item whose spec isn't one known here is left out, instead of
 * failing the import of the whole legend over it.
 *
 * @param item
 */
function mapSupportingItemExportToProto(
  item: InventoryItemSupportingItemProto.AsObject,
): InventoryItemSupportingItemProto[] {
  const Spec = InventoryItemSupportingItemProto.Spec
  const spec = new Spec()
  const source: InventoryItemSupportingItemSpecExport | undefined = item.spec
  if (source?.document) {
    spec.setDocument(
      new Spec.Document().setTypeShortId(source.document.typeShortId),
    )
  } else if (source?.personalInfo) {
    spec.setPersonalInfo(new Spec.PersonalInfo())
  } else if (source?.spousalInfo) {
    spec.setSpousalInfo(new Spec.SpousalInfo())
  } else if (source?.dependentsInfo) {
    spec.setDependentsInfo(new Spec.DependentsInfo())
  } else if (source?.personalRefundMethod || source?.personalRefundMethodInfo) {
    spec.setPersonalRefundMethod(new Spec.PersonalRefundMethod())
  } else if (source?.businessInfo) {
    spec.setBusinessInfo(new Spec.BusinessInfo())
  } else if (source?.businessRefundMethod || source?.businessRefundMethodInfo) {
    spec.setBusinessRefundMethod(new Spec.BusinessRefundMethod())
  } else if (source?.notes) {
    spec.setNotes(new Spec.Notes())
  } else {
    return []
  }
  return [
    new InventoryItemSupportingItemProto()
      .setSpec(spec)
      .setRequired(item.required),
  ]
}

function mapTypeSpecificationExportToProto(
  typeSpecification: InventoryItemTypeSpecificationProto.AsObject,
): InventoryItemTypeSpecificationProto {
  const TypeOptions = InventoryItemTypeSpecificationProto.TypeOptions
  const spec = typeSpecification.spec
  const options = new TypeOptions()
  if (spec?.pb_default) {
    options.setDefault(new Empty())
  } else if (spec?.bookkeeping) {
    options.setBookkeeping(
      new TypeOptions.Bookkeeping().setRecurring(spec.bookkeeping.recurring),
    )
  } else if (spec?.consultation) {
    options.setConsultation(
      new TypeOptions.Consultation().setLengthInMinutes(
        spec.consultation.lengthInMinutes,
      ),
    )
  } else if (spec?.tax) {
    options.setTax(
      new TypeOptions.Tax()
        .setReviewPrice(mapDecimalExportToProto(spec.tax.reviewPrice))
        .setReviewProPrice(mapDecimalExportToProto(spec.tax.reviewProPrice)),
    )
  } else if (spec?.taxExtension) {
    const extension = new TypeOptions.TaxExtension()
      .setType(spec.taxExtension.type)
      .setBillingType(spec.taxExtension.billingType)
    // Without billing info, the server resets the billing type to unknown.
    if (spec.taxExtension.billingInfo) {
      extension.setBillingInfo(
        mapExtensionBillingInfoExportToProto(spec.taxExtension.billingInfo),
      )
    }
    options.setTaxExtension(extension)
  } else {
    throw new Error('Invalid inventory item type specification provided')
  }
  return new InventoryItemTypeSpecificationProto().setSpec(options)
}

function mapExtensionBillingInfoExportToProto(
  billingInfo: InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo.AsObject,
): InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo {
  const BillingInfo =
    InventoryItemTypeSpecificationProto.TaxExtensionBillingInfo
  const proto = new BillingInfo()
  if (billingInfo.regular) {
    proto.setRegular(
      new BillingInfo.RegularBillingInfo()
        .setClientChargeAmount(
          mapDecimalExportToProto(billingInfo.regular.clientChargeAmount),
        )
        .setProPayoutAmount(
          mapDecimalExportToProto(billingInfo.regular.proPayoutAmount),
        ),
    )
  } else if (billingInfo.jobProgression?.billingInfo) {
    proto.setJobProgression(
      new BillingInfo.JobProgressionBillingInfo().setBillingInfo(
        mapJobProgressionBillingInfoExportToProto(
          billingInfo.jobProgression.billingInfo,
        ),
      ),
    )
  } else {
    proto.setUnspecified(new Empty())
  }
  return proto
}

function mapJobProgressionBillingInfoExportToProto(
  billingInfo: InventoryItemJobProgressionBillingInfoProto.AsObject,
): InventoryItemJobProgressionBillingInfoProto {
  const { outsourcing, retail, essential, preparation, review } = billingInfo
  const proto = new InventoryItemJobProgressionBillingInfoProto()
  if (outsourcing) {
    proto.setOutsourcing(
      new InventoryItemJobProgressionOutsourcingProto()
        .setJobStarted(mapStepExportToProto(outsourcing.jobStarted))
        .setOpenItems(mapStepExportToProto(outsourcing.openItems))
        .setDraftInReview(mapStepExportToProto(outsourcing.draftInReview))
        .setDraftRejected(mapStepExportToProto(outsourcing.draftRejected))
        .setDraftApproved(mapStepExportToProto(outsourcing.draftApproved)),
    )
  }
  if (retail) {
    proto.setRetail(
      new InventoryItemJobProgressionRetailProto()
        .setJobStarted(mapStepExportToProto(retail.jobStarted))
        .setOpenItems(mapStepExportToProto(retail.openItems))
        .setDraftInReview(mapStepExportToProto(retail.draftInReview))
        .setDraftRejected(mapStepExportToProto(retail.draftRejected))
        .setDraftApproved(mapStepExportToProto(retail.draftApproved))
        .setAuthorizationSigned(
          mapStepExportToProto(retail.authorizationSigned),
        )
        .setReturnFiled(mapStepExportToProto(retail.returnFiled)),
    )
  }
  if (essential) {
    proto.setEssential(
      new InventoryItemJobProgressionEssentialProto()
        .setJobStarted(mapStepExportToProto(essential.jobStarted))
        .setDeliverableCompleted(
          mapStepExportToProto(essential.deliverableCompleted),
        ),
    )
  }
  if (preparation) {
    proto.setPreparation(
      new InventoryItemJobProgressionPreparationProto()
        .setJobStarted(mapStepExportToProto(preparation.jobStarted))
        .setOpenItems(mapStepExportToProto(preparation.openItems))
        .setPreparationCompleted(
          mapStepExportToProto(preparation.preparationCompleted),
        ),
    )
  }
  if (review) {
    proto.setReview(
      new InventoryItemJobProgressionReviewProto()
        .setJobStarted(mapStepExportToProto(review.jobStarted))
        .setOpenItems(mapStepExportToProto(review.openItems))
        .setDraftInReview(mapStepExportToProto(review.draftInReview))
        .setDraftRejected(mapStepExportToProto(review.draftRejected))
        .setDraftApproved(mapStepExportToProto(review.draftApproved))
        .setReviewCompleted(mapStepExportToProto(review.reviewCompleted)),
    )
  }
  return proto
}

function mapStepExportToProto(
  step: ProgressionStepBillingValuesProto.AsObject | undefined,
): ProgressionStepBillingValuesProto | undefined {
  return (
    step &&
    new ProgressionStepBillingValuesProto()
      .setClientChargeAmount(mapDecimalExportToProto(step.clientChargeAmount))
      .setProPayoutAmount(mapDecimalExportToProto(step.proPayoutAmount))
  )
}

function mapDecimalExportToProto(decimal: DecimalProto.AsObject | undefined) {
  return new DecimalProto()
    .setUnits(decimal?.units ?? 0)
    .setNanos(decimal?.nanos ?? 0)
}

function mapTimestampExportToProto(timestamp: Timestamp.AsObject) {
  return new Timestamp().setSeconds(timestamp.seconds).setNanos(timestamp.nanos)
}
